import React, { useEffect, useState } from 'react';
import { Loader2, User, Hash, Phone, Calendar, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { recordAccessApi } from '../api/recordAccess.api';
import RecordAccessOtpPanel from './RecordAccessOtpPanel';

/** Locked access modal — basic demographics + visit history; OTP unlocks full details. */
export default function PatientLockedAccessModal({
  open, patientId, patientName, queueEntryId = '', onClose, onUnlocked,
}) {
  const [data, setData] = useState(null);
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOtp, setShowOtp] = useState(false);

  useEffect(() => {
    if (!open || !patientId) return;
    let cancelled = false;
    setLoading(true);
    setShowOtp(false);
    Promise.all([
      recordAccessApi.limited(patientId),
      recordAccessApi.status(patientId, queueEntryId),
    ])
      .then(([lim, st]) => {
        if (cancelled) return;
        setData(lim);
        setAccess(st);
        if (st.level === 'full') onUnlocked?.();
      })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, patientId, queueEntryId]);

  const p = data?.patient;
  const visits = data?.visits || [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="patient-locked-access-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#071B34]">
            <Lock className="h-5 w-5 text-[#F15A22]" />
            Locked for access
          </DialogTitle>
          <DialogDescription className="text-[#5A6B7C]">
            {p?.name || patientName || 'Patient'} — full chart and prescriptions need OTP.
            Only today&apos;s incomplete appointments open without verification.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-zinc-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
        ) : (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-2 text-sm text-[#5A6B7C]">
              <div className="flex items-center gap-2"><Hash className="h-3.5 w-3.5" /> {p?.patient_number || '—'}</div>
              <div className="flex items-center gap-2">
                <User className="h-3.5 w-3.5" />
                {[p?.age && `${p.age} yrs`, p?.gender].filter(Boolean).join(' · ') || '—'}
              </div>
              {p?.phone ? <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {p.phone}</div> : null}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-[#071B34] mb-2">Visit history</h3>
              {visits.length === 0 ? (
                <p className="text-sm text-zinc-400">No prior visits on file.</p>
              ) : (
                <ul className="space-y-2 max-h-40 overflow-auto">
                  {visits.map((v) => (
                    <li key={v.id} className="rounded-lg border border-[#E8EEF4] px-3 py-2 text-sm flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5 text-[#C4A35A] shrink-0" />
                      <span className="font-medium text-[#071B34]">{v.date || '—'}</span>
                      <span className="text-[#5A6B7C]">{v.time}</span>
                      <span className="text-[#5A6B7C] truncate">Dr. {v.doctor_name}</span>
                      <span className="ml-auto text-xs text-zinc-500">{v.status_label || v.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {!showOtp ? (
              <Button
                onClick={() => setShowOtp(true)}
                className="w-full text-white"
                style={{ background: '#F15A22' }}
                data-testid="request-otp-btn"
              >
                Request OTP
              </Button>
            ) : (
              <RecordAccessOtpPanel
                patientId={patientId}
                channels={access?.channels || []}
                onUnlocked={() => onUnlocked?.()}
              />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
